import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const OCCASIONS = [
  "Corporate / Office",
  "Hotel / Resort",
  "Wedding",
  "Birthday",
  "Anniversary",
  "Baby Shower",
  "Festival",
  "Return Gift",
  "Other",
];

const PRICE_OPTIONS = [15, 20, 30, 35]; // ₹ per piece

export default function EnquiryForm({ open, onOpenChange, product }) {
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    occasion: OCCASIONS[0],
    quantity: 100,
    price_per_piece: PRICE_OPTIONS[0],
    message: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      return toast.error("Please fill name, email and phone");
    }
    if (form.quantity < 100) return toast.error("Minimum order is 100 chocolates");
    if (!PRICE_OPTIONS.includes(form.price_per_piece)) return toast.error("Please pick a chocolate price");
    if (!date) return toast.error("Please pick when the chocolates are required");
    setLoading(true);
    try {
      await api.post("/enquiries", {
        chocolate_id: product?.id || null,
        chocolate_name: product?.name || "",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        required_date: date.toISOString(),
        address: form.address.trim(),
        occasion: form.occasion,
        quantity: form.quantity,
        price_per_piece: form.price_per_piece,
        message: form.message.trim(),
      });
      toast.success("Enquiry sent! Our team will contact you shortly.");
      onOpenChange(false);
      // reset
      setDate(undefined);
      setForm({ name: "", email: "", phone: "", address: "", occasion: OCCASIONS[0], quantity: 100, price_per_piece: PRICE_OPTIONS[0], message: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send enquiry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Send an enquiry</DialogTitle>
          <DialogDescription>
            {product ? (
              <>Interested in <span className="font-medium text-[#2a2928]">{product.name}</span>? Share a few details and our team will get in touch. Minimum order is 100 chocolates.</>
            ) : (
              <>Share a few details and our team will get in touch. Minimum order is 100 chocolates.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Your name</Label>
            <Input data-testid="enq-name" className="mt-2 rounded-xl" value={form.name} onChange={(e) => setField("name", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="enq-phone" className="mt-2 rounded-xl" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Email</Label>
            <Input data-testid="enq-email" type="email" className="mt-2 rounded-xl" value={form.email} onChange={(e) => setField("email", e.target.value)} />
          </div>
          <div>
            <Label>Occasion</Label>
            <select
              data-testid="enq-occasion"
              className="mt-2 w-full rounded-xl border border-[#e8e2da] bg-white px-3 py-2 text-sm"
              value={form.occasion}
              onChange={(e) => setField("occasion", e.target.value)}
            >
              {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <Label>Order quantity (min 100)</Label>
            <Input
              data-testid="enq-quantity"
              type="number"
              min={100}
              className="mt-2 rounded-xl"
              value={form.quantity}
              onChange={(e) => setField("quantity", Math.max(100, parseInt(e.target.value || "100", 10)))}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Chocolate price (per piece)</Label>
            <select
              data-testid="enq-price"
              className="mt-2 w-full rounded-xl border border-[#e8e2da] bg-white px-3 py-2 text-sm"
              value={form.price_per_piece}
              onChange={(e) => setField("price_per_piece", parseInt(e.target.value, 10))}
            >
              {PRICE_OPTIONS.map((p) => (
                <option key={p} value={p}>₹{p} per chocolate</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Required by</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="enq-date"
                  type="button"
                  className="mt-2 flex w-full items-center justify-between rounded-xl border border-[#e8e2da] bg-white px-4 py-2.5 text-left text-sm"
                >
                  {date ? format(date, "PPP") : "Pick a date"}
                  <CalendarIcon className="h-4 w-4 text-[#7a7571]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="md:col-span-2">
            <Label>Delivery address</Label>
            <Textarea data-testid="enq-address" rows={2} className="mt-2 rounded-xl" value={form.address} onChange={(e) => setField("address", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Notes (design, wrapper style, etc.)</Label>
            <Textarea data-testid="enq-message" rows={3} className="mt-2 rounded-xl" value={form.message} onChange={(e) => setField("message", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="enq-submit" onClick={submit} disabled={loading} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Send enquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
